/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, FieldErrors, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import ReservationDetailPhoneWithCountrySelect from 'pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailPhoneWithCountrySelect'
import CompanyContactAutocomplete from 'components/CompanyContactAutocomplete'

import { filteredCountryList, PhoneCountry } from 'utils/countryUtils'
import { europeanCountryCodes } from 'utils/europeanCountries'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'

import { Company } from 'types/entities/Company'
import { CompanyContact } from 'types/entities/CompanyContact'
import { pruneEmpty, mergeContactAfterSave } from 'utils/companyUtils'
import { normalizeCompanyOrigin } from 'utils/companies/companyOriginUtils'
import { closeSecondaryModal, closeModal } from 'helpers/actions'

import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import CompanyPositionSelect from './CompanyPositionSelect'
import CompanyOriginSelect from './CompanyOriginSelect'
import LeadTagsAutocomplete from 'components/leads/forms/LeadTagsAutocomplete'
import UiSellerSelect from 'components/leads/UiSellerSelect'
import UiTabSwitcher, { UiTabSwitcherItem } from 'components/Ui/UiTabSwitcher'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'

import { CompanyContactInputs, useCompanyContactSchema } from 'components/Companies/schemas/useCompanyContactSchema'
import { useRequiredField } from 'utils/bookingUtils'
import { UiTabVariant } from 'types/enums/UiTabVariant'
import { ContactFormTab } from 'types/enums/specific/ContactFormTab'
import { useFormUIState } from 'utils/hooks/useCompanyFormUI'
import { useFormSubmitHandler } from 'utils/formSubmitUtils'
import { CompanyContactFormProps } from 'types/entities/specific/CompanyFormTypes'
import { getSaveAndCloseLabel } from 'utils/utility'
import { toast } from 'utils/toastUtils'
import {
  isGenericCompanyContact,
  isGenericContactEmail,
} from 'utils/contacts/genericContactUtils'
import clsx from 'clsx'

const CompanyContactForm = (props: CompanyContactFormProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const company: Company | null = useTypedSelector((state) => state.companiesState.selectedCompany)

  const { isSubmitting, setIsSubmitting } = useFormUIState()
  const { handleSubmitSuccess, handleSubmitError, handleCancel } = useFormSubmitHandler()

  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(
    filteredCountryList(europeanCountryCodes)[30]
  )
  const [activeTab, setActiveTab] = useState<ContactFormTab>(
    props.selectFromExisting ? ContactFormTab.Existing : ContactFormTab.New
  )
  const [selectedExistingContact, setSelectedExistingContact] = useState<CompanyContact | null>(null)
  const [currentContact, setCurrentContact] = useState<CompanyContact | null>(props.contact || null)
  const getInitialSellerId = (contact: CompanyContact | undefined) =>
    contact?.seller_id ?? props.fallbackSellerId ?? null

  const schema = useCompanyContactSchema()
  const { testField } = useRequiredField(schema)

  const emptyContactValues: CompanyContactInputs = {
    title: '',
    custom_title: '',
    gender: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    origin: '',
    tags: [],
    seller_id: null,
    note: '',
    is_generic: false,
  }

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    reset,
    control,
    formState: { errors },
    watch,
  } = useForm<CompanyContactInputs>({
    resolver: zodResolver(schema),
    // Never prefill a new person from Account email/phone — that is how Angela inherited Tobias.
    defaultValues: props.contact
      ? {
          title: props.contact.title || '',
          custom_title: props.contact.custom_title || '',
          gender: props.contact.gender || '',
          first_name: props.contact.first_name || '',
          last_name: props.contact.last_name || '',
          email: props.contact.email || '',
          phone: props.contact.phone || '',
          position: props.contact.position || '',
          origin: props.contact.origin || '',
          tags: props.contact.tags || [],
          seller_id: getInitialSellerId(props.contact),
          note: props.contact.note || '',
          is_generic: isGenericCompanyContact(props.contact),
        }
      : emptyContactValues,
  })

  const isGeneric = Boolean(watch('is_generic'))
  const watchedEmail = watch('email')

  useEffect(() => {
    if (!watchedEmail?.trim()) {
      return
    }
    if (isGenericContactEmail(watchedEmail)) {
      setValue('is_generic', true, { shouldValidate: true })
    }
  }, [watchedEmail, setValue])

  const clearContactForm = () => {
    reset(emptyContactValues)
    setSelectedExistingContact(null)
  }

  const handleTabChange = (id: string) => {
    const nextTab = id as ContactFormTab
    setActiveTab(nextTab)
    // Switching Existing → Create new must not keep the previously selected person's email/phone.
    if (nextTab === ContactFormTab.New) {
      clearContactForm()
    }
  }

  const onSubmit = async (data: CompanyContactInputs, shouldClose: boolean = false) => {
    setIsSubmitting(true)

    try {
      const contactId =
        activeTab === ContactFormTab.Existing ? selectedExistingContact?.id : currentContact?.id

      const contactData: Partial<CompanyContact> = pruneEmpty(
        {
        ...data,
        id: contactId,
        title: data.is_generic ? '' : data.title?.trim() || '',
        custom_title: data.is_generic ? '' : data.custom_title?.trim() || '',
        gender: data.is_generic ? '' : data.gender || '',
        first_name: data.first_name?.trim() || '',
        last_name: data.last_name?.trim() || '',
        email: data.email?.trim() || '',
        phone: data.phone?.trim() || '',
        position: data.is_generic ? '' : data.position?.trim() || '',
        origin: normalizeCompanyOrigin(data.origin) || '',
        tags: Array.isArray(data.tags) ? data.tags : [],
        seller_id:
          data.seller_id != null
            ? Number(data.seller_id)
            : getInitialSellerId(currentContact || undefined),
        note: data.note?.trim() || '',
        is_generic: Boolean(data.is_generic),
        company_id: company?.id || currentContact?.company_id,
      },
        ['title', 'custom_title', 'position', 'tags', 'is_generic', 'first_name', 'last_name', 'seller_id']
      )

      const response = await dispatch(
        CompaniesAction.fetchCreateOrUpdateContact(contactData as CompanyContact)
      )

      if (response) {
        const mergedContact = mergeContactAfterSave(contactData, response)
        handleSubmitSuccess({
          useSecondaryClose: props.useSecondaryClose,
          inline: props.inline,
          successMessage: t('Contact saved successfully'),
        })
        setCurrentContact(mergedContact)
        props.onChange(mergedContact, false)
        if (shouldClose && !props.inline) {
          setTimeout(() => {
            if (props.useSecondaryClose || props.useSecondaryClose === undefined) {
              dispatch(closeSecondaryModal())
            } else {
              dispatch(closeModal())
            }
          }, 0)
        }
      }
    } catch (err) {
      handleSubmitError(err, t('Failed to save contact information'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleExistingContactSelect = (contact: CompanyContact | null) => {
    setSelectedExistingContact(contact)
    if (!contact) {
      clearContactForm()
      return
    }

    setValue('title', contact.title || '')
    setValue('custom_title', contact.custom_title || '')
    setValue('gender', contact.gender || '')
    setValue('first_name', contact.first_name || '')
    setValue('last_name', contact.last_name || '')
    setValue('email', contact.email || '')
    setValue('phone', contact.phone || '')
    setValue('position', contact.position || '')
    setValue('origin', contact.origin || '')
    setValue('tags', contact.tags || [])
    setValue('seller_id', contact.seller_id ?? null)
    setValue('note', contact.note || '')
    setValue('is_generic', isGenericCompanyContact(contact))
  }

  useEffect(() => {
    if (props.contact) {
      const { title, first_name, last_name, email, phone, position, origin, tags, note } = props.contact

      setCurrentContact(props.contact)
      reset({
        title: title || '',
        custom_title: props.contact.custom_title || '',
        gender: props.contact.gender || '',
        first_name: first_name || '',
        last_name: last_name || '',
        email: email || '',
        phone: phone || '',
        position: position || '',
        origin: origin || '',
        tags: tags || [],
        seller_id: getInitialSellerId(props.contact),
        note: note || '',
        is_generic: isGenericCompanyContact(props.contact),
      })
      setSelectedExistingContact(null)
    } else {
      setCurrentContact(null)
      clearContactForm()
    }
    // clearContactForm/reset are stable enough for contact identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contact])

  const setContactKind = (generic: boolean) => {
    setValue('is_generic', generic, { shouldValidate: true, shouldDirty: true })
    if (errors.first_name || errors.last_name) void trigger(['first_name', 'last_name'])
    if (generic) {
      setValue('position', '', { shouldValidate: true })
    }
  }

  const tabs: UiTabSwitcherItem[] = [
    { id: ContactFormTab.Existing, name: t('Select from existing') },
    { id: ContactFormTab.New, name: t('Create new contact') },
  ]

  const isCreateNewMode = () => {
    return !props.selectFromExisting || activeTab === ContactFormTab.New
  }

  const isSelectExistingMode = () => {
    return props.selectFromExisting && activeTab === ContactFormTab.Existing
  }

  const saveAndCloseText = getSaveAndCloseLabel(
    props.showSaveAndClose,
    isSubmitting,
    t('Saving contact...'),
    t('Save & Close')
  )

  const handleInvalidSubmit = (validationErrors: FieldErrors<CompanyContactInputs>) => {
    const fieldNames: Partial<Record<keyof CompanyContactInputs, string>> = {
      first_name: t(isGeneric ? 'Contact name' : 'First Name'),
      last_name: t(isGeneric ? 'Contact label' : 'Last Name'),
      email: t('Email'),
      phone: t('Phone'),
      seller_id: t('Seller'),
    }
    const invalidFields = Object.keys(validationErrors)
      .map((field) => fieldNames[field as keyof CompanyContactInputs])
      .filter((field): field is string => Boolean(field))

    toast.error(
      invalidFields.length
        ? t('Please fill in required fields: {{fields}}', { fields: invalidFields.join(', ') })
        : t('Please fix validation errors')
    )
  }

  return (
    <form onSubmit={(e) => e.preventDefault()} autoComplete="off" className="CompanyContactForm flex flex-col grow">
      {props.selectFromExisting && (
        <div className="px-4 pt-4 mb-6">
          <UiTabSwitcher
            items={tabs}
            selected={activeTab}
            onClick={handleTabChange}
            variant={UiTabVariant.Underline}
          />
        </div>
      )}

      <UiFormInner>
        <UiFormGrid cols={12} gap={6} responsive={false} className="grid-cols-12">
          {isSelectExistingMode() && (
            <div className="col-span-12">
              <CompanyContactAutocomplete
                label={t('Select Existing Contact')}
                name="existing_contact"
                placeholder={t('Search for existing contact...')}
                onSelect={handleExistingContactSelect}
                required={true}
              />
            </div>
          )}

          {isCreateNewMode() && (
            <>
              <div className="col-span-12 space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('Contact type')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={clsx(
                      'rounded-md border px-3 py-1.5 text-sm transition-colors',
                      !isGeneric
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-blue-500 bg-white text-blue-700 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-200'
                    )}
                    onClick={() => setContactKind(false)}
                  >
                    {t('Person')}
                  </button>
                  <button
                    type="button"
                    className={clsx(
                      'rounded-md border px-3 py-1.5 text-sm transition-colors',
                      isGeneric
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-blue-500 bg-white text-blue-700 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-200'
                    )}
                    onClick={() => setContactKind(true)}
                  >
                    {t('Generic contact')}
                  </button>
                </div>
                {isGeneric ? (
                  <>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t(
                        'Company-level mailbox or phone (e.g. info@, kontakt@, hr@) — not a person.'
                      )}
                    </p>
                    <CompanyFormInput
                      className="col-span-12 sm:col-span-6"
                      label={t('Contact name')}
                      name="first_name"
                      register={register}
                      errors={errors}
                      required
                      placeholder={t('Enter contact name')}
                    />
                    <CompanyFormInput
                      className="col-span-12 sm:col-span-6"
                      label={t('Contact label')}
                      name="last_name"
                      register={register}
                      errors={errors}
                      required
                      placeholder={t('Enter contact label')}
                    />
                  </>
                ) : null}
              </div>

              {!isGeneric ? (
                <>
                  <CompanyFormInput
                    className="col-span-12 sm:col-span-6"
                    label={t('First Name')}
                    name="first_name"
                    register={register}
                    errors={errors}
                    required={!isGeneric}
                    placeholder={t('Enter first name')}
                  />

                  <CompanyFormInput
                    className="col-span-12 sm:col-span-6"
                    label={t('Last Name')}
                    name="last_name"
                    register={register}
                    errors={errors}
                    required={!isGeneric}
                    placeholder={t('Enter last name')}
                  />
                  <Controller
                    name="gender"
                    control={control}
                    render={({ field }) => (
                      <UiSelectWithGraphics
                        className="col-span-12 sm:col-span-6"
                        label={t('Gender')}
                        selected={field.value || ''}
                        setSelected={field.onChange}
                        options={[
                          { value: 'male', label: t('Male') },
                          {
                            value: 'female',
                            label: t('Female'),
                          },
                          {
                            value: 'other',
                            label: t('Other'),
                          },
                        ]}
                      />
                    )}
                  />
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <UiSelectWithGraphics
                        className="col-span-12 sm:col-span-6"
                        label={t('Title')}
                        selected={field.value || ''}
                        setSelected={(value) => {
                          const nextTitle = String(value ?? '')
                          field.onChange(nextTitle)
                          if (nextTitle !== 'other') {
                            setValue('custom_title', '', { shouldValidate: true })
                          }
                        }}
                        options={[
                          { value: '', label: t('None') },
                          { value: 'Dr.', label: t('Dr.') },
                          { value: 'Prof.', label: t('Prof.') },
                          { value: 'Prof. Dr.', label: t('Prof. Dr.') },
                          { value: 'other', label: t('Other') },
                        ]}
                      />
                    )}
                  />
                  {watch('title') === 'other' && (
                    <CompanyFormInput
                      className="col-span-12 sm:col-span-6"
                      label={t('Custom Title')}
                      name="custom_title"
                      register={register}
                      errors={errors}
                      placeholder={t('Enter custom title')}
                    />
                  )}
                </>
              ) : null}

              <div className="col-span-12 grid grid-cols-12 gap-6">
                <CompanyFormInput
                  className="col-span-12 sm:col-span-6"
                  label={t('Email')}
                  name="email"
                  type="email"
                  register={register}
                  errors={errors}
                  required={testField('email')}
                  placeholder={isGeneric ? 'info@company.ch' : t('company@example.com')}
                />

                <ReservationDetailPhoneWithCountrySelect
                  className="col-span-12 sm:col-span-6"
                  options={filteredCountryList(europeanCountryCodes) as PhoneCountry[]}
                  defaultCountry={selectedCountry}
                  value={watch('phone')}
                  register={register}
                  name="phone"
                  label={t('Phone')}
                  setValue={(v: string) => setValue('phone' as keyof CompanyContactInputs, v)}
                  setSelectedCountry={setSelectedCountry}
                  errors={errors}
                  required={testField('phone')}
                />
              </div>
              {!isGeneric ? (
                <Controller
                  name="position"
                  control={control}
                  render={({ field }) => (
                    <CompanyPositionSelect
                      className="col-span-12 sm:col-span-6"
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors.position?.message}
                      required={testField('position')}
                      errors={errors}
                    />
                  )}
                />
              ) : null}

              <Controller
                name="origin"
                control={control}
                render={({ field }) => (
                  <CompanyOriginSelect
                    className="col-span-12 sm:col-span-6"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={errors.origin?.message}
                    required={testField('origin')}
                    errors={errors}
                  />
                )}
              />

              <UiFormField
                className="col-span-12 sm:col-span-6"
                label={t('Contact Owner')}
                required={true}
                error={errors.seller_id?.message}
              >
                <Controller
                  control={control}
                  name="seller_id"
                  render={({ field }) => (
                    <UiSellerSelect
                      selected={field.value ?? null}
                      setSelected={(val) =>
                        field.onChange(val != null && val !== '' ? Number(val) : null)
                      }
                      autoSelectCurrentUser={!currentContact?.id}
                    />
                  )}
                />
              </UiFormField>

              <div className="col-span-12">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('Tags')}
                </label>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <LeadTagsAutocomplete
                      selectedTags={field.value || []}
                      onTagsChange={field.onChange}
                      placeholder={t('Type to search or add tags...')}
                    />
                  )}
                />
              </div>

              <CompanyFormInput
                className="col-span-12"
                label={t('Contact Notes')}
                name="note"
                component="textarea"
                register={register}
                errors={errors}
                required={testField('note')}
                placeholder={t('Enter note')}
              />
            </>
          )}
        </UiFormGrid>
      </UiFormInner>

      <UiFormActionsBar
        primaryId={'btn-companies-wizard-contact-save'}
        primaryText={isSubmitting ? t('Saving contact...') : t('Save')}
        onPrimary={handleSubmit((data) => onSubmit(data, false), handleInvalidSubmit)}
        saveAndCloseText={saveAndCloseText}
        onSaveAndClose={
          props.showSaveAndClose
            ? handleSubmit((data) => onSubmit(data, true), handleInvalidSubmit)
            : undefined
        }
        secondaryId={!props.inline ? 'btn-companies-wizard-contact-cancel' : undefined}
        secondaryText={!props.inline ? (props.onSkip ? t('Skip') : t('Cancel')) : undefined}
        onSecondary={
          !props.inline
            ? props.onSkip || (() => handleCancel({ useSecondaryClose: props.useSecondaryClose, inline: props.inline }))
            : undefined
        }
        backText={t('Back')}
        onBack={props.onBack}
        loading={isSubmitting}
        disabled={isSelectExistingMode() && !selectedExistingContact}
      />
    </form>
  )
}

export default CompanyContactForm
